import { createContext, useContext, useState, useEffect, useCallback } from 'react'
import {
    getToken,
    getCachedUser,
    removeToken,
    apiLogin,
    apiGetMe,
    apiGetCapabilities,
    setCachedUser,
    apiImpersonate,
    apiViewAs,
    apiViewAsTargets,
    apiStopImpersonating,
} from '../api/client'
import { setSentryUser, clearSentryUser } from '../sentry'

const AuthContext = createContext(null)

/* ── Dev auto-login: skip login screen on localhost ──
 *
 * `import.meta.env.DEV` — не украшение, а условие существования этой ветки.
 * Оно статически false в собранном бандле, поэтому Rollup выбрасывает и
 * проверку, и `DEV_ADMIN_USER` целиком: в прод-сборке обхода нет вовсе.
 *
 * Без него хватало ОДНОГО hostname. Прод-бандл, открытый с localhost, выдавал
 * платформенного администратора с `has_all: true` без единого пароля — и это
 * уезжало в каждую сборку. Поймано на приёмке 2026-09-01: локальный стенд
 * подставлял фиктивного админа поверх настоящего входа, и проверка прав
 * показывала не то, что показал бы прод.
 */
const DEV_AUTO_LOGIN =
    import.meta.env.DEV &&
    (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')

const DEV_ADMIN_USER = {
    id: 'dev-admin',
    login: 'admin',
    full_name: 'Администратор (dev)',
    email: 'admin@onboardix.local',
    role: 'admin',
    is_platform_admin: true,
    admin_scope: 'platform',
    admin_scope_label: 'Платформа Onboardix',
    extra_modules: [],
    allowed_modules: [], // empty = show all (fallback in filterByModules)
    custom_role_label: '',
    is_active: true,
}

/**
 * AuthProvider with fallback:
 * - If backend is reachable → JWT auth
 * - If backend is down → offline mode with cached user or auto-login as admin
 * - If DEV_AUTO_LOGIN → instant admin access without credentials
 */
export function AuthProvider({ children }) {
    const [user, setUser] = useState(null)
    const [capabilities, setCapabilities] = useState(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)
    const [offlineMode, setOfflineMode] = useState(false)
    const [isImpersonating, setIsImpersonating] = useState(false)
    // Test accounts this user may "view as" (non-admin, by binding). Empty for most users.
    const [viewAsTargets, setViewAsTargets] = useState([])

    // Check token on mount
    useEffect(() => {
        setIsImpersonating(!!localStorage.getItem('onboardix_original_token'))
        // Обход пишет фиктивного админа в localStorage, а ветка `else if (cached)`
        // ниже поднимает кэшированного пользователя ДАЖЕ БЕЗ ТОКЕНА. Значит
        // прогон dev-сборки на том же origin оставлял бы админа, которого потом
        // воскрешала прод-сборка. Убираем след прежде, чем его прочитают.
        if (!DEV_AUTO_LOGIN && getCachedUser()?.id === DEV_ADMIN_USER.id) {
            removeToken()
        }
        // On localhost — instant admin access, skip all auth checks
        if (DEV_AUTO_LOGIN) {
            setCachedUser(DEV_ADMIN_USER)
            setUser(DEV_ADMIN_USER)
            setCapabilities({ permissions: ['all'], has_all: true, policy_version: 'dev-bypass' })
            setSentryUser(DEV_ADMIN_USER)
            setOfflineMode(true)
            setLoading(false)
            return
        }

        const token = getToken()
        const cached = getCachedUser()

        if (token) {
            apiGetMe()
                .then(async u => {
                    setUser(u)
                    setCachedUser(u)
                    setSentryUser(u)
                    try {
                        const caps = await apiGetCapabilities()
                        setCapabilities(caps)
                    } catch {
                        setCapabilities(null)
                    }
                    setLoading(false)
                })
                .catch(err => {
                    if (err.isOffline || err.message.includes('недоступен') || err.message.includes('офлайн')) {
                        if (cached) {
                            setUser(cached)
                            setCapabilities({ permissions: [], has_all: cached?.role === 'admin', policy_version: 'offline-cache' })
                            setOfflineMode(true)
                        }
                    } else {
                        removeToken()
                        setUser(null)
                        setCapabilities(null)
                    }
                    setLoading(false)
                })
        } else if (cached) {
            setUser(cached)
            setCapabilities({ permissions: [], has_all: cached?.role === 'admin', policy_version: 'offline-cache' })
            setOfflineMode(true)
            setLoading(false)
        } else {
            setCapabilities(null)
            setLoading(false)
        }
    }, [])

    // Load the "view as" allow-list once a real user is established (online only).
    // Empty for admins and anyone without bindings — the topbar entry stays hidden.
    useEffect(() => {
        if (!user?.id || offlineMode) { setViewAsTargets([]); return }
        let cancelled = false
        apiViewAsTargets()
            .then(res => { if (!cancelled) setViewAsTargets(Array.isArray(res?.targets) ? res.targets : []) })
            .catch(() => { if (!cancelled) setViewAsTargets([]) })
        return () => { cancelled = true }
    }, [user?.id, offlineMode])

    const refreshUser = useCallback(async () => {
        const token = getToken()
        if (!token || offlineMode) return;
        try {
            const u = await apiGetMe()
            let caps = null
            try {
                caps = await apiGetCapabilities()
            } catch {
                caps = null
            }
            setUser(u)
            setCapabilities(caps)
            setCachedUser(u)
            setSentryUser(u)
            console.info('[AuthContext] User profile refreshed seamlessly');
            return u
        } catch (err) {
            console.error('[AuthContext] Seamless refresh failed:', err)
        }
    }, [offlineMode])

    // Listen for forced logout and refresh events
    useEffect(() => {
        const logoutHandler = () => { setUser(null); setCapabilities(null); setOfflineMode(false) }
        const refreshHandler = () => { refreshUser() }
        window.addEventListener('auth:logout', logoutHandler)
        window.addEventListener('auth:refresh', refreshHandler)
        return () => {
            window.removeEventListener('auth:logout', logoutHandler)
            window.removeEventListener('auth:refresh', refreshHandler)
        }
    }, [refreshUser])

    const login = useCallback(async (loginStr, password) => {
        setError(null)
        try {
            const data = await apiLogin(loginStr, password)
            setUser(data.user)
            try {
                const caps = await apiGetCapabilities()
                setCapabilities(caps)
            } catch {
                setCapabilities(null)
            }
            setSentryUser(data.user)
            setOfflineMode(false)
            // ModuleContext подписан на это событие — мгновенно подтягивает
            // свежий каталог модулей (HR-юзер не упрётся в stale localStorage
            // cache из старой сессии без новых HH-пунктов).
            window.dispatchEvent(new Event('auth:login'))
            return data
        } catch (err) {
            // If backend is unreachable, allow offline demo login
            if (err.isOffline || err.message.includes('недоступен') || err.message.includes('офлайн')) {
                const offlineUser = {
                    id: 'offline-' + loginStr,
                    login: loginStr,
                    full_name: loginStr === 'admin' ? 'Администратор (офлайн)' : loginStr,
                    email: `${loginStr}@onboardix.local`,
                    role: loginStr === 'admin' ? 'admin' : 'employee',
                    extra_modules: [],
                    allowed_modules: [], // empty = show all (fallback)
                    custom_role_label: '',
                    is_active: true,
                }
                setCachedUser(offlineUser)
                setUser(offlineUser)
                setCapabilities({ permissions: [], has_all: offlineUser.role === 'admin', policy_version: 'offline-login' })
                setOfflineMode(true)
                return { user: offlineUser }
            }
            setError(err.message)
            throw err
        }
    }, [])

    const logout = useCallback(() => {
        removeToken()
        clearSentryUser()
        setUser(null)
        setCapabilities(null)
        setOfflineMode(false)
    }, [])

    const impersonate = useCallback(async (userId) => {
        setError(null)
        try {
            const data = await apiImpersonate(userId)
            setUser(data.user)
            try {
                const caps = await apiGetCapabilities()
                setCapabilities(caps)
            } catch {
                setCapabilities(null)
            }
            setSentryUser(data.user)
            setIsImpersonating(true)
            // Force reload to apply specific modules properly across all app states
            window.location.href = '/erp/' 
            return data
        } catch (err) {
            setError(err.message)
            throw err
        }
    }, [])

    // Non-admin "view as" a bound test account. Mirrors impersonate; exit via the
    // same stopImpersonating() flow (shared onboardix_original_token + banner).
    const viewAs = useCallback(async (userId) => {
        setError(null)
        try {
            const data = await apiViewAs(userId)
            setUser(data.user)
            try {
                const caps = await apiGetCapabilities()
                setCapabilities(caps)
            } catch {
                setCapabilities(null)
            }
            setSentryUser(data.user)
            setIsImpersonating(true)
            window.location.href = '/erp/'
            return data
        } catch (err) {
            setError(err.message)
            throw err
        }
    }, [])

    const stopImpersonating = useCallback(async () => {
        setError(null)
        try {
            const data = await apiStopImpersonating()
            setUser(data)
            try {
                const caps = await apiGetCapabilities()
                setCapabilities(caps)
            } catch {
                setCapabilities(null)
            }
            setSentryUser(data)
            setIsImpersonating(false)
            // Force reload to get back to original state cleanly
            window.location.href = '/erp/'
            return data
        } catch (err) {
            setError(err.message)
            throw err
        }
    }, [])

    // Принять готовую сессию, выданную не формой логина (вход через Telegram).
    // Токен и пользователь уже положены в хранилище слоем api/auth — здесь
    // повторяется ровно тот же «хвост», что и у входа паролем: capabilities,
    // Sentry, событие auth:login для ModuleContext. Разъехаться им нельзя,
    // иначе вошедший через Telegram получит пустое меню.
    const adoptSession = useCallback(async (data) => {
        setError(null)
        setUser(data.user)
        try {
            setCapabilities(await apiGetCapabilities())
        } catch {
            setCapabilities(null)
        }
        setSentryUser(data.user)
        setOfflineMode(false)
        window.dispatchEvent(new Event('auth:login'))
        return data
    }, [])

    const hasPermission = useCallback((permission) => {
        if (!user) return false
        if (user.role === 'admin' || user.is_platform_admin) return true
        if (!permission) return false
        if (!capabilities) return false
        if (capabilities.has_all) return true
        const perms = Array.isArray(capabilities.permissions) ? capabilities.permissions : []
        return perms.includes(permission)
    }, [user, capabilities])

    return (
        <AuthContext.Provider value={{
            user, loading, error, login, logout, refreshUser, adoptSession,
            capabilities, hasPermission,
            isAuthenticated: !!user, offlineMode, impersonate, stopImpersonating, isImpersonating,
            viewAs, viewAsTargets
        }}>
            {children}
        </AuthContext.Provider>
    )
}

export function useAuth() {
    const ctx = useContext(AuthContext)
    if (!ctx) throw new Error('useAuth must be inside AuthProvider')
    return ctx
}
